import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Table, createDataSource, useDatasource } from "components/Bable"
import {
  activateMarketTargets,
  deactivateMarketTargets,
  deleteMarketTargets,
  fetchMarketTargets,
  patchExternalCode,
} from "api/targets"
import { queryCache, useMutation, useQuery } from "react-query"

import { ApiError } from "api/http/utils"
import Button from "ui/buttons/Button"
import { CreateTargetModal } from "./CreateTarget"
import { EmptyState } from "components/EmptyState"
import { Loading } from "components/Loading"
import { MarketTarget } from "models/targets"
import React from "react"
import { Search } from "routes/Base/Admin/components/Search"
import { Surface } from "components/Surface"
import { columns } from "./columns"
import { css } from "@emotion/css"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

const getRowId = (x: MarketTarget) => x?.id

export const globalState = createDataSource({ getRowId })

function Targets() {
  const { t } = useI18n()
  const { meta } = useAuthenticated()
  const marketId = meta?.layerId ?? ""

  const [modalInfo, setModalInfo] = React.useState<ModalInfo>()
  const [searchValue, setSearchValue] = React.useState<string>("")
  const [open, setOpen] = React.useState<boolean>(false)
  const isModalOpen = Boolean(modalInfo)
  const { hasAuthority } = useAuthenticated()
  const actionAllowed = hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])
  React.useEffect(() => globalState.setErrors({}), [])

  // ** fetch targets
  const { isLoading } = useQuery(["targets", marketId], fetchMarketTargets, {
    onSuccess: (d: MarketTarget[]) => {
      globalState.setData(d)
    },
  })

  const [activate] = useMutation(activateMarketTargets, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("targets")
    },
    onError: () => setModalInfo(undefined),
  })

  const [deactivate] = useMutation(deactivateMarketTargets, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("targets")
    },
    onError: () => setModalInfo(undefined),
  })

  const [remove] = useMutation(deleteMarketTargets, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("targets")
    },
    onError: () => setModalInfo(undefined),
  })

  const [patch] = useMutation(patchExternalCode, {
    onSuccess: (d: MarketTarget) => globalState.updateRow(d),
    onError: (e: ApiError, mf: Partial<MarketTarget>) => {
      const error = e as ApiError
      const errorMap = error?.errorsMap ?? {}
      !!mf.id && globalState.setRowErrors(mf?.id, errorMap)
    },
  })

  const handleSearchChange = (
    evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = evt.target
    setSearchValue(() => value)
  }

  const handleSearchClear = () => {
    setSearchValue(() => "")
  }

  const source = useDatasource({
    state: globalState,
    filters: [
      {
        active: !!searchValue.trim(),
        predicate: (row) => {
          const reg = new RegExp(searchValue, "ig")
          return reg.test(row?.name || "")
        },
      },
    ],
  })

  const handleDelete = React.useCallback(
    (row: MarketTarget) => {
      setModalInfo({
        title: t("admin.targets.delete.title"),
        type: "primary_destructive",
        description: t("admin.targets.delete.description"),
        submitLabel: t("admin.targets.delete.action"),
        onSubmit: async () => await remove({ targetId: row.id || "" }),
      })
    },
    [t, remove],
  )

  const onUpdate = React.useCallback(
    async (row, label, value) => {
      patch({ id: row.id, [label]: value } as MarketTarget)
    },
    [patch],
  )

  const handleActivate = React.useCallback(
    (row: MarketTarget) => {
      setModalInfo({
        title: t("admin.targets.activate.title"),
        description: t("admin.targets.activate.description"),
        submitLabel: t("modal.label.activate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () => await activate({ targetId: row.id || "" }),
      })
    },
    [activate, t],
  )

  const handleDeactivate = React.useCallback(
    (row: MarketTarget) => {
      setModalInfo({
        type: "primary_destructive",
        title: t("admin.targets.deactivate.title"),
        description: t("admin.targets.deactivate.description"),
        submitLabel: t("modal.label.deactivate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () => await deactivate({ targetId: row.id || "" }),
      })
    },
    [deactivate, t],
  )

  const handleClick = () => setOpen(true)

  return (
    <Surface className={styles.surface} mb={2}>
      <div className={styles.tableToolbar}>
        <Search
          name={"name"}
          value={searchValue}
          handleChange={handleSearchChange}
          handleClear={handleSearchClear}
          placeholder={t("inputs.search.placeholder")}
        />
        {hasAuthority(["AUTH_CREATE_TARGET"]) && (
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={handleClick}
              disabled={open}
            >
              {t("admin.targets.create_button")}
            </Button>
          </div>
        )}
      </div>
      {isLoading ? (
        <Loading />
      ) : (
        <Table<MarketTarget>
          columns={columns(
            handleActivate,
            handleDeactivate,
            handleDelete,
            onUpdate,
            actionAllowed,
          )}
          source={source}
          height={400}
          className={styles.table}
          emptyText={
            <EmptyState title={t("market-finance.targets.title")}></EmptyState>
          }
        />
      )}
      <ConfirmationModal
        onCancel={() => setModalInfo(undefined)}
        open={isModalOpen}
        {...modalInfo}
      />
      <CreateTargetModal
        open={open}
        onCancel={() => {
          setModalInfo(undefined)
          setOpen(false)
        }}
      />
    </Surface>
  )
}

export default Targets

const styles = {
  tableToolbar: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 0 var(--spacing-xxl-2);

    .left {
      display: flex;
      align-items: center;
      & > :not(:first-child) {
        margin-left: var(--spacing-m);
      }
    }
  `,
  table: css`
    .RebaseTable__header-cell {
      padding: 0 12px;
      font-size: 12px;
      color: var(--black);
      font-weight: bold;
      text-transform: capitalize;
    }
    .RebaseTable__grid-cell {
    }

    .RebaseTable__body-row:hover {
      background-color: var(--primary-light);
    }

    .RebaseTable__body-row--hovered {
      background-color: var(--primary-light);
    }

    .RebaseTable__cell {
      &[data-column-id^="__"] {
        border-right: none;
        padding: 0;
      }
      :last-child {
        display: flex;
        justify-content: center;
      }
    }
    .RebaseTable__cell:focus-within {
      box-shadow: none;
    }
  `,
  surface: css`
    padding: var(--spacing-xxl-2) var(--spacing-m) var(--spacing-m);
  `,
}
